import React from "react";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { Divider, TextField, Tooltip } from "@mui/material";

const toSelectValue = (raw) => (typeof raw === "string" ? raw : "");

/**
 * Keep the Select menu open when the user chooses "Add Custom Status"
 * or interacts with the custom status TextField. MUI Select otherwise
 * treats MenuItem clicks as a selection and closes immediately.
 */
export const preventSelectCloseOnMouseDown = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

export const normalizeCustomStatusInput = (raw) => {
  if (typeof raw !== "string") {
    return "";
  }
  return raw.trim();
};

/**
 * State changes when the user chooses "Add Custom Status".
 * keepOpenForCustom is a one-shot flag for the MenuItem→onClose race only.
 */
export const beginCustomStatusEntry = () => ({
  keepOpenForCustom: true,
  showCustomInput: true,
  openSelect: true,
});

/**
 * Decide what happens when MUI Select fires onClose.
 * First close after "Add Custom Status" re-opens and consumes the keep-open flag
 * so a later outside-click / Escape can dismiss without saving.
 */
export const resolveSelectClose = ({ keepOpenForCustom }) => {
  if (keepOpenForCustom) {
    return {
      openSelect: true,
      keepOpenForCustom: false, // consume — next outside-click may close
      showCustomInput: true,
      resetCustomInput: false,
    };
  }

  return {
    openSelect: false,
    keepOpenForCustom: false,
    showCustomInput: false,
    resetCustomInput: true,
  };
};

/**
 * Commit typed custom status (Enter). Blank/whitespace is ignored.
 */
export const commitCustomStatusEntry = (raw) => {
  const trimmedValue = normalizeCustomStatusInput(raw);
  return {
    value: trimmedValue || null,
    keepOpenForCustom: false,
    showCustomInput: false,
    openSelect: false,
    customInputValue: "",
  };
};

const EditableInternalStatusCell = ({
  row,
  value: initialValue,
  updateMyData, // This is a custom function that we supplied to our table instance
  cellData, // This is a custom data that we supplied to our table instance
}) => {
  const [value, setValue] = React.useState(() => toSelectValue(initialValue));
  const selectValue = toSelectValue(value);
  const internalStatuses = cellData?.internal_status || [];
  const [showCustomInput, setShowCustomInput] = React.useState(false);
  const [customInputValue, setCustomInputValue] = React.useState("");
  const [openSelect, setOpenSelect] = React.useState(false);
  // Ref so onClose (same click cycle as "Add Custom Status") can keep the menu open.
  const keepOpenForCustomRef = React.useRef(false);

  React.useEffect(() => {
    if (initialValue && typeof initialValue === "string") {
      const trimmed = initialValue.trim();
      if (trimmed === "") {
        setValue("");
      } else {
        const found = internalStatuses.find(
          (status) => status.name.toLowerCase() === trimmed.toLowerCase()
        );
        setValue(found ? found.name : trimmed);
      }
    } else {
      setValue("");
    }
  }, [internalStatuses, initialValue]);

  const resetCustomInputState = () => {
    keepOpenForCustomRef.current = false;
    setShowCustomInput(false);
    setCustomInputValue("");
  };

  const handleUpdateData = (updateValue) => {
    updateMyData(row, updateValue, "customer_internal_status");
  };

  const onChange = (e) => {
    e.stopPropagation();
    if (e.target.value) {
      setValue(e.target.value);
      handleUpdateData(e.target.value);
      resetCustomInputState();
    }
  };

  const onOpen = () => {
    setOpenSelect(true);
  };

  const onClose = (e) => {
    e?.stopPropagation?.();
    // Clicking "Add Custom Status" still triggers Select onClose; keep open for typing.
    const next = resolveSelectClose({
      keepOpenForCustom: keepOpenForCustomRef.current,
    });
    keepOpenForCustomRef.current = next.keepOpenForCustom;
    setOpenSelect(next.openSelect);
    if (next.resetCustomInput) {
      setShowCustomInput(false);
      setCustomInputValue("");
    } else {
      setShowCustomInput(next.showCustomInput);
    }
  };

  const onCellClick = (ev) => {
    ev.stopPropagation();
    setOpenSelect(true);
  };

  const handleCustomInputChange = (e) => {
    setCustomInputValue(e.target.value);
  };

  const addCustomStatus = () => {
    const next = commitCustomStatusEntry(customInputValue);
    if (next.value) {
      setValue(next.value);
      handleUpdateData(next.value);
    }
    keepOpenForCustomRef.current = next.keepOpenForCustom;
    setShowCustomInput(next.showCustomInput);
    setCustomInputValue(next.customInputValue);
    setOpenSelect(next.openSelect);
  };

  const handleCustomInputKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      addCustomStatus();
    }
  };

  const handleAddCustomClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = beginCustomStatusEntry();
    keepOpenForCustomRef.current = next.keepOpenForCustom;
    setShowCustomInput(next.showCustomInput);
    setOpenSelect(next.openSelect);
    // If mousedown preventDefault already blocked Select onClose, consume the
    // one-shot after this event turn so the next outside-click can dismiss.
    setTimeout(() => {
      keepOpenForCustomRef.current = false;
    }, 0);
  };

  return (
    <Tooltip
      title={selectValue.trim() !== "" ? selectValue : ""}
      disableHoverListener={selectValue.trim() === ""}
    >
      <div
        onClick={onCellClick}
        onKeyDown={onCellClick}
        role="button"
        tabIndex="0"
        style={{ cursor: "default", pointerEvents: "auto" }}
      >
        <Select
          id="multiple-role-id"
          size="small"
          value={selectValue}
          onChange={onChange}
          onOpen={onOpen}
          onClose={onClose}
          onKeyDown={(e) => {
            if (showCustomInput) {
              e.stopPropagation();
            }
          }}
          open={openSelect}
          variant="outlined"
          displayEmpty
          className="w-[150px] border-green-100 outline-red-100"
          renderValue={(val) => {
            if (!val || val.trim() === "") {
              return <span style={{ color: "#999" }}>Select from list</span>;
            }
            return val;
          }}
          sx={{
            borderRadius: "5px",
            height: 35,
            minHeight: 35,
            padding: "4px",
            "& .MuiSelect-select": {
              padding: "4px 8px",
              borderColor: "red",
            },
          }}
          MenuProps={{
            // Avoid stealing focus from the custom status TextField.
            disableAutoFocusItem: true,
          }}
        >
          {internalStatuses.map((status) => (
            <MenuItem key={status.id} value={status.name}>
              {status.name}
            </MenuItem>
          ))}
          {internalStatuses.length > 0 && <Divider />}
          <MenuItem
            key="add-custom"
            onMouseDown={preventSelectCloseOnMouseDown}
            onClick={handleAddCustomClick}
            className="text-blue-700 text-bold"
          >
            <em>Add Custom Status</em>
          </MenuItem>
          {showCustomInput && (
            <MenuItem
              key="custom-input"
              onMouseDown={preventSelectCloseOnMouseDown}
              onClick={(e) => e.stopPropagation()}
              sx={{ padding: "0px" }}
            >
              <TextField
                autoFocus
                value={customInputValue}
                onChange={handleCustomInputChange}
                onKeyDown={handleCustomInputKeyDown}
                onClick={(e) => e.stopPropagation()}
                placeholder="Enter custom status..."
                variant="standard"
                fullWidth
                inputProps={{ "aria-label": "Enter custom status" }}
                sx={{
                  bgcolor: "#ffffff",
                  "&:hover": { bgcolor: "#f0f0f0" },
                  padding: "8px 16px",
                }}
              />
            </MenuItem>
          )}
        </Select>
      </div>
    </Tooltip>
  );
};

export default EditableInternalStatusCell;
